import React, { createContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { apiClient } from '@/api/ApiClient';
import bootstrapEn from '../locales/bootstrap.en.json';
import bootstrapCs from '../locales/bootstrap.cs.json';
import bootstrapEsOverrides from '../locales/bootstrap.es.json';

/** API row + dynamic UI language code */
export interface LanguageOption {
  code: string;
  nativeName: string;
}

export type Language = string;

const BOOTSTRAP_BUNDLES: Record<string, Record<string, string>> = {
  en: bootstrapEn as Record<string, string>,
  cs: bootstrapCs as Record<string, string>,
  /** Spanish: sparse overrides merged onto EN so offline fallback stays complete. */
  es: {
    ...(bootstrapEn as Record<string, string>),
    ...(bootstrapEsOverrides as Record<string, string>),
  },
};

const LANGUAGE_STORAGE_KEY = 'dreamboyfriend_language';
const etagStorageKey = (lang: string) => `translationsEtag_${lang}`;
const bundleStorageKey = (lang: string) => `translationsBundle_${lang}`;

const FALLBACK_LANGUAGES: LanguageOption[] = [
  { code: 'en', nativeName: 'English' },
  { code: 'cs', nativeName: 'Čeština' },
];

function getBootstrap(lang: string): Record<string, string> {
  return BOOTSTRAP_BUNDLES[lang] || BOOTSTRAP_BUNDLES['en'];
}

function mergeBootstrap(lang: string, api: Record<string, string>): Record<string, string> {
  return { ...getBootstrap(lang), ...api };
}

function normalizeEtag(etag: string | null | undefined): string | undefined {
  if (etag == null || etag === '') return undefined;
  const t = etag.trim();
  if (t.length >= 2 && t.startsWith('"') && t.endsWith('"')) {
    return t.slice(1, -1);
  }
  return t;
}

function pickInitialLanguage(codes: string[]): string {
  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (saved && codes.includes(saved)) {
    return saved;
  }
  const browserLang = navigator.language.toLowerCase();
  const byBrowser = codes.find((c) => browserLang === c || browserLang.startsWith(`${c}-`));
  const picked = byBrowser ?? (codes.includes('en') ? 'en' : codes[0] ?? 'en');
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, picked);
  } catch {
    /* storage unavailable */
  }
  return picked;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  languageOptions: LanguageOption[];
  translations: Record<string, string>;
  translationsOffline: boolean;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>(FALLBACK_LANGUAGES);
  const [language, setLanguageState] = useState<string>(() =>
    pickInitialLanguage(FALLBACK_LANGUAGES.map((l) => l.code)),
  );

  const apiLayerRef = useRef<Record<string, Record<string, string>>>({});
  const [translations, setTranslations] = useState<Record<string, string>>(() => {
    const lang = pickInitialLanguage(FALLBACK_LANGUAGES.map((l) => l.code));
    return mergeBootstrap(lang, readCachedBundle(lang) || {});
  });
  const [translationsOffline, setTranslationsOffline] = useState(false);

  function readCachedBundle(lang: string): Record<string, string> | null {
    try {
      const raw = sessionStorage.getItem(bundleStorageKey(lang));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Record<string, string>;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let list: LanguageOption[] = FALLBACK_LANGUAGES;
      try {
        const data = (await apiClient.getLanguages()) as LanguageOption[];
        if (Array.isArray(data) && data.length > 0) {
          list = data;
        }
      } catch {
        /* use FALLBACK_LANGUAGES */
      }
      if (cancelled) return;
      setLanguageOptions(list);
      const codes = list.map((l) => l.code);
      setLanguageState((prev) => {
        if (codes.includes(prev)) {
          return prev;
        }
        const next = pickInitialLanguage(codes);
        localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cached = readCachedBundle(language);
    if (cached) {
      apiLayerRef.current[language] = cached;
    }
    setTranslations(mergeBootstrap(language, apiLayerRef.current[language] || {}));

    let cancelled = false;
    (async () => {
      try {
        const storedEtag = sessionStorage.getItem(etagStorageKey(language)) || undefined;
        const res = await apiClient.getTranslations(language, {
          ifNoneMatch: storedEtag,
        });

        if (cancelled) return;

        if (res.notModified) {
          const fromSession = readCachedBundle(language);
          if (fromSession) {
            apiLayerRef.current[language] = fromSession;
            setTranslations(mergeBootstrap(language, fromSession));
            setTranslationsOffline(false);
            return;
          }
          sessionStorage.removeItem(etagStorageKey(language));
          const full = await apiClient.getTranslations(language, {});
          if (cancelled) return;
          if (full.notModified || !full.data) {
            setTranslations(mergeBootstrap(language, {}));
            setTranslationsOffline(true);
            return;
          }
          const body = full.data as Record<string, string>;
          apiLayerRef.current[language] = body;
          try {
            sessionStorage.setItem(bundleStorageKey(language), JSON.stringify(body));
          } catch {
            /* quota */
          }
          const ne = normalizeEtag(full.etag);
          if (ne) sessionStorage.setItem(etagStorageKey(language), ne);
          setTranslations(mergeBootstrap(language, body));
          setTranslationsOffline(false);
          return;
        }

        const body = res.data as Record<string, string>;
        apiLayerRef.current[language] = body;
        try {
          sessionStorage.setItem(bundleStorageKey(language), JSON.stringify(body));
        } catch {
          /* quota */
        }
        const ne = normalizeEtag(res.etag);
        if (ne) {
          sessionStorage.setItem(etagStorageKey(language), ne);
        }
        setTranslations(mergeBootstrap(language, body));
        setTranslationsOffline(false);
      } catch {
        if (cancelled) return;
        const fromSession = readCachedBundle(language);
        if (fromSession) {
          apiLayerRef.current[language] = fromSession;
          setTranslations(mergeBootstrap(language, fromSession));
        } else {
          setTranslations(mergeBootstrap(language, apiLayerRef.current[language] || {}));
        }
        setTranslationsOffline(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [language]);

  const syncLanguageFromServer = useCallback((lang: string | null | undefined) => {
    if (!lang || typeof lang !== 'string' || !lang.trim()) return;
    const c = lang.trim().toLowerCase();
    setLanguageState(c);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, c);
  }, []);

  const setLanguage = useCallback(
    (lang: Language) => {
      if (languageOptions.length > 0 && !languageOptions.some((l) => l.code === lang)) {
        return;
      }
      setLanguageState(lang);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
      void (async () => {
        try {
          if (apiClient.token) {
            await apiClient.updatePreferredLanguage(lang);
          }
        } catch {
          /* offline or guest */
        }
      })();
    },
    [languageOptions],
  );

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, syncLanguageFromServer, languageOptions, translations, translationsOffline }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

